// ---------------------------------------------------------------------------
// PWA_ControlBillet — Auth Service
// Handles login, token refresh, and logout via JWT HttpOnly cookies.
// ---------------------------------------------------------------------------

import { useAuthStore } from '@/store/auth-store';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getCsrfToken(): string {
  const match = document.cookie.match(/csrf_access_token=([^;]+)/);
  return match ? match[1] : '';
}

// ---------------------------------------------------------------------------
// Service
// ---------------------------------------------------------------------------

export const authService = {
  /**
   * Authenticate with email + password.
   * The backend sets JWT in HttpOnly cookies on success.
   */
  async login(email: string, password: string) {
    const res = await fetch('/api/auth/login', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });

    const data = await res.json();

    if (!res.ok) {
      throw new Error(data.message || data.error || 'Identifiants incorrects');
    }

    if (data.requires_2fa) {
      throw new Error('2FA non supporte sur cette interface');
    }

    return data;
  },

  /**
   * Refresh the access token using the refresh cookie.
   * Calls logout on failure to clear stale state.
   */
  async refresh() {
    const res = await fetch('/api/auth/refresh', {
      method: 'POST',
      credentials: 'include',
      headers: { 'X-CSRF-TOKEN': getCsrfToken() },
    });

    if (!res.ok) {
      useAuthStore.getState().logout();
      throw new Error('Session expiree');
    }

    return res.json();
  },

  /**
   * Log out — clears store and calls backend logout endpoint.
   */
  async logout() {
    useAuthStore.getState().logout();
  },
};
